import { trackBeginCheckout } from "@/lib/analytics";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { Card } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Lightbulb, CalendarClock, Rocket, Loader2, Sparkles } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuthState } from "@/hooks/useAuthState";
import { useExpertSupportState } from "@/hooks/useExpertSupportState";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  semaforoColor,
  type DiagnosisInput, type DiagnosisResult, type Semaforo,
} from "@/lib/location-diagnosis";
import SectorAuthorityCard from "@/components/diagnosticador/SectorAuthorityCard";

interface Props {
  input: DiagnosisInput;
  result: DiagnosisResult;
  /** Compact mode for the history accordion (smaller spacing & type, hides upsell). */
  compact?: boolean;
}

const fmtEuro = (n: number) =>
  n.toLocaleString("es-ES", { maximumFractionDigits: 0 }) + " €";
const fmtSignedEuro = (n: number) =>
  (n >= 0 ? "+" : "−") + fmtEuro(Math.abs(n));

const DynamicSimulator = ({ input, result, compact = false }: Props) => {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthState();
  const { state: tierState } = useExpertSupportState();
  const es = lang === "es";

  const ticket = Math.max(1, input.averageTicket || 1);
  const dias = Math.max(1, input.diasApertura || 26);
  const unitCost = result.costePersonalUnitario || 1850;

  // Initial weekly hours from previous "empleados" estimate (1 empleado = 40h)
  const initialHours = Math.max(0, Math.min(400, (result.empleadosEstimados || 1) * 40));
  const initialMargin = Math.round((result.margenBruto || 0.5) * 100);
  const initialOverhead = Math.max(100, result.overheadFijo || 400);

  // Default tickets: smallest count that puts profit comfortably in the Green zone (> +3.000€)
  const computeGreenTickets = (marginPctVal: number, hoursVal: number, overheadVal: number) => {
    const fixed = input.requestedRent + (unitCost / 40) * hoursVal + overheadVal;
    const perTicket = ticket * dias * (marginPctVal / 100);
    if (perTicket <= 0) return 60;
    const needed = Math.ceil((fixed + 3001) / perTicket);
    return Math.min(150, Math.max(1, needed));
  };

  const [tickets, setTickets] = useState<number>(
    computeGreenTickets(initialMargin, initialHours, initialOverhead),
  );
  const [marginPct, setMarginPct] = useState<number>(initialMargin);
  const [hoursWeek, setHoursWeek] = useState<number>(initialHours);
  const [overhead, setOverhead] = useState<number>(initialOverhead);
  const [loadingHours, setLoadingHours] = useState(false);

  useEffect(() => {
    const m = Math.round((result.margenBruto || 0.5) * 100);
    const h = Math.max(0, Math.min(400, (result.empleadosEstimados || 1) * 40));
    const o = Math.max(100, result.overheadFijo || 400);
    setMarginPct(m);
    setHoursWeek(h);
    setOverhead(o);
    setTickets(computeGreenTickets(m, h, o));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, result]);

  // Labor cost: agreement unit cost is for 40h/week → divide by 40 then × hoursWeek
  const costeLaboral = (unitCost / 40) * hoursWeek;
  const costesFijos = input.requestedRent + costeLaboral + overhead;
  const facturacion = tickets * ticket * dias;
  const beneficio = facturacion * (marginPct / 100) - costesFijos;

  const semaforo: Semaforo =
    beneficio < -2000 ? "Rojo" : beneficio <= 3000 ? "Amarillo" : "Verde";
  const semColor = semaforoColor(semaforo);

  const statusKey =
    semaforo === "Verde" ? "simStatusGreen" : semaforo === "Amarillo" ? "simStatusYellow" : "simStatusRed";
  const statusMsg = (t("locationDiagnosis", statusKey) as string)
    .replace("{amount}", fmtSignedEuro(beneficio));

  const lowTicket = ticket < 10;

  const handleBuyHours = async () => {
    if (!isAuthenticated) {
      navigate(es ? "/mentoria-retail#extras" : "/en/mentoring#extras");
      return;
    }
    setLoadingHours(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "create-extra-hours-checkout",
        { body: { hours: 1 } },
      );
      if (error) throw error;
      if (data?.url) {
        trackBeginCheckout({ source: "diagnosticador", productType: "extra_hours", productKey: "1h", interval: "one_time" });
        window.open(data.url, "_blank");
      }
    } catch (err: any) {
      toast.error(err?.message || (es ? "No se pudo iniciar el pago" : "Could not start checkout"));
    } finally {
      setLoadingHours(false);
    }
  };

  const goToMentoring = () =>
    navigate(es ? "/mentoria-retail" : "/en/mentoring");

  return (
    <div className={compact ? "space-y-4" : "space-y-6"}>
      {/* ── Big traffic light circle + status ─────────────────────────── */}
      <div className="flex flex-col items-center text-center py-4">
        <div
          className="rounded-full transition-colors"
          style={{
            width: compact ? 90 : 120,
            height: compact ? 90 : 120,
            background: `radial-gradient(circle at 30% 30%, ${semColor}, ${semColor}cc 60%, ${semColor}88 100%)`,
            boxShadow: `0 0 40px ${semColor}66, 0 0 80px ${semColor}33, inset 0 -8px 20px rgba(0,0,0,0.18)`,
          }}
          aria-label={semaforo}
        />
        <p
          className={`mt-4 max-w-xl font-semibold leading-snug ${compact ? "text-xs" : "text-sm"}`}
          style={{ color: semColor }}
        >
          {statusMsg}
        </p>
      </div>

      {/* Low-ticket conditional tip */}
      {lowTicket && (
        <div className="flex items-start gap-2 rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2">
          <Lightbulb className="h-4 w-4 text-foreground mt-0.5 shrink-0" strokeWidth={1.75} />
          <p className="text-[12px] text-yellow-900 leading-snug">
            {t("locationDiagnosis", "tipLowTicket")}
          </p>
        </div>
      )}

      {/* ── Sector authority card ───────────────────────────────────── */}
      <SectorAuthorityCard detailedSectorId={input.detailedSectorId} />

      {/* ── Minimal main card: 3 KPIs + tickets slider only ─────────── */}
      <Card className={compact ? "p-4" : "p-6"}>
        <div className="grid grid-cols-3 gap-3 mb-6">
          <KpiBox label={t("locationDiagnosis", "simCardTickets")} value={`${tickets}`} />
          <KpiBox label={t("locationDiagnosis", "simCardRevenue")} value={fmtEuro(facturacion)} />
          <KpiBox
            label={t("locationDiagnosis", "simCardResult")}
            value={fmtSignedEuro(beneficio)}
            color={semColor}
            highlight
          />
        </div>

        <div>
          <div className="flex items-baseline justify-between mb-2">
            <Label className="text-xs text-muted-foreground">
              {t("locationDiagnosis", "sliderTickets")}
            </Label>
            <span className="text-sm font-semibold">{tickets}</span>
          </div>
          <Slider
            value={[tickets]}
            min={0}
            max={150}
            step={1}
            onValueChange={(v) => setTickets(v[0] ?? 0)}
          />
          <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
            <span>0</span><span>150</span>
          </div>
        </div>
      </Card>

      {/* ── Advanced accordion: 3 sliders + live breakdown ───────────── */}
      <Accordion type="single" collapsible>
        <AccordionItem value="adv" className="border rounded-lg px-4 bg-muted/20">
          <AccordionTrigger className="text-sm font-semibold hover:no-underline">
            {t("locationDiagnosis", "advancedAccordionTitle")}
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-5 pt-2">
              <SliderRow
                label={t("locationDiagnosis", "sliderMargin")}
                value={marginPct}
                display={`${marginPct}%`}
                min={10}
                max={90}
                step={1}
                onChange={setMarginPct}
              />
              <div>
                <div className="flex items-baseline justify-between mb-1">
                  <Label className="text-[11px] text-muted-foreground">
                    {t("locationDiagnosis", "sliderEmployees")}
                  </Label>
                  <span className="text-xs font-semibold">{hoursWeek}h</span>
                </div>
                <Slider
                  value={[hoursWeek]}
                  min={0}
                  max={400}
                  step={10}
                  onValueChange={(v) => setHoursWeek(v[0] ?? 0)}
                />
                <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
                  <span>0h</span>
                  <span>{t("locationDiagnosis", "sliderEmployeesHint")}</span>
                  <span>400h</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {es ? "Coste laboral estimado:" : "Estimated labor cost:"}{" "}
                  <span className="font-semibold text-foreground">{fmtEuro(costeLaboral)}/mes</span>
                </p>
              </div>
              <SliderRow
                label={t("locationDiagnosis", "sliderOverhead")}
                value={overhead}
                display={fmtEuro(overhead)}
                min={100}
                max={3000}
                step={50}
                onChange={setOverhead}
              />

              {/* Live breakdown table */}
              <div className="rounded-lg border bg-card p-3 mt-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  {es ? "Desglose en tiempo real" : "Live breakdown"}
                </p>
                <div className="space-y-1.5 text-xs">
                  <Row label={t("locationDiagnosis", "breakdownRent")} value={fmtEuro(input.requestedRent)} />
                  <Row
                    label={(t("locationDiagnosis", "breakdownStaff") as string)
                      .replace("{hours}", String(hoursWeek))
                      .replace("{unit}", fmtEuro(unitCost))}
                    value={fmtEuro(costeLaboral)}
                  />
                  <Row label={t("locationDiagnosis", "breakdownOverhead")} value={fmtEuro(overhead)} />
                  <Row label={t("locationDiagnosis", "breakdownFixedTotal")} value={fmtEuro(costesFijos)} bold />
                  <Row label={t("locationDiagnosis", "breakdownMargin")} value={`${marginPct}%`} />
                  <Row
                    label={t("locationDiagnosis", "breakdownBreakEven")}
                    value={fmtEuro(costesFijos / Math.max(0.01, marginPct / 100))}
                    bold
                    accent
                  />
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* ── Help & conversion module (tier-aware) ─────────────────────── */}
      {!compact && (
        <div className="pt-2">
          <h3 className="text-center text-base sm:text-lg font-bold mb-4 px-4">
            {t("locationDiagnosis", "upsellHeading")}
          </h3>

          {tierState === "vip" ? (
            // Active mentoring — single priority support card
            <Card className="p-5 flex flex-col border-yellow-300 bg-yellow-50/40 max-w-xl mx-auto">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-5 w-5 text-yellow-700" strokeWidth={1.5} />
                <h4 className="font-bold text-sm">{t("locationDiagnosis", "upsellActiveTitle")}</h4>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t("locationDiagnosis", "upsellActiveText")}
              </p>
              <Button
                className="mt-4 w-full rounded-full font-semibold bg-yellow-400 text-yellow-950 hover:bg-yellow-500"
                onClick={() => navigate(es ? "/mentoria-retail#agenda" : "/en/mentoring#agenda")}
              >
                {t("locationDiagnosis", "upsellActiveCta")}
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Card 1 — Expert session (price varies LITE vs PRO) */}
              <Card className="p-5 flex flex-col h-full border-border">
                <div className="flex items-center gap-2 mb-2">
                  <CalendarClock className="h-5 w-5 text-foreground" strokeWidth={1.5} />
                  <h4 className="font-bold text-sm">{t("locationDiagnosis", "upsellExpertTitle")}</h4>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed flex-1">
                  {t("locationDiagnosis", "upsellExpertText")}
                </p>
                <p className="mt-3 text-sm font-bold text-foreground">
                  {tierState === "panel"
                    ? t("locationDiagnosis", "upsellExpertPricePro")
                    : t("locationDiagnosis", "upsellExpertPriceLite")}
                </p>
                <Button
                  variant="outline"
                  className="mt-4 w-full rounded-full font-semibold"
                  onClick={handleBuyHours}
                  disabled={loadingHours}
                >
                  {loadingHours ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {t("locationDiagnosis", "upsellExpertCta")}
                </Button>
              </Card>

              {/* Card 2 — Mentoring programs */}
              <Card className="p-5 flex flex-col h-full border-yellow-300 bg-yellow-50/40">
                <div className="flex items-center gap-2 mb-2">
                  <Rocket className="h-5 w-5 text-yellow-700" strokeWidth={1.5} />
                  <h4 className="font-bold text-sm">{t("locationDiagnosis", "upsellMentoringTitle")}</h4>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed flex-1">
                  {t("locationDiagnosis", "upsellMentoringTextV2")}
                </p>
                <Button
                  className="mt-4 w-full rounded-full font-semibold bg-yellow-400 text-yellow-950 hover:bg-yellow-500"
                  onClick={goToMentoring}
                >
                  {t("locationDiagnosis", "upsellMentoringCtaV2")}
                </Button>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const KpiBox = ({
  label, value, color, highlight,
}: { label: string; value: string; color?: string; highlight?: boolean }) => (
  <div
    className="rounded-lg p-3 text-center"
    style={highlight && color ? { backgroundColor: `${color}1A` } : undefined}
  >
    <p className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p
      className="text-lg sm:text-xl font-bold mt-1 leading-tight"
      style={color ? { color } : undefined}
    >
      {value}
    </p>
  </div>
);

const SliderRow = ({
  label, value, display, min, max, step, onChange, hint,
}: {
  label: string;
  value: number;
  display: string;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  hint?: string;
}) => (
  <div>
    <div className="flex items-baseline justify-between mb-1">
      <Label className="text-[11px] text-muted-foreground">{label}</Label>
      <span className="text-xs font-semibold text-foreground">{display}</span>
    </div>
    <Slider
      value={[value]}
      min={min}
      max={max}
      step={step}
      onValueChange={(v) => onChange(v[0] ?? min)}
    />
    <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
      <span>{min}</span>
      {hint ? <span>{hint}</span> : <span>{max}</span>}
    </div>
  </div>
);

const Row = ({
  label, value, bold, accent,
}: { label: string; value: string; bold?: boolean; accent?: boolean }) => (
  <div className={`flex justify-between border-b last:border-b-0 pb-1 ${bold ? "font-semibold" : ""} ${accent ? "text-yellow-900" : ""}`}>
    <span className={bold ? "" : "text-muted-foreground"}>{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);

export default DynamicSimulator;
